import { Instant, LocalDate } from "@js-joda/core";
import type { OfferActionType } from "./OfferAction";
import { DateSubmissionFormat } from "src/shared/formatters/DateTimeSubmissionFormat";
import Term from "src/shared/term/objects/Term";
import type TermNameType from "src/shared/term/objects/TermNameType";
import { ConvertToInstant } from "src/utilities/DateUtils";

export default class Offer {
  public offerUUID: string;
  public merchantUUID: string;
  public offerName: string;
  public action: OfferActionType;
  public smsBody1: string;
  public smsBodyRegistered: string;
  public smsBodyCoolDown: string;
  public description: string;
  public logoOverride: boolean;
  public logoUrl: string;
  public offerStartDate: LocalDate;
  public offerEndDate: LocalDate;
  public firstCreated: Instant;
  public lastUpdated: Instant;
  public offerStatus: boolean;
  public offerStaticUrl: string;
  public googleTagId?: string;
  public isGoogleSignup: boolean;
  public isFacebookSignup: boolean;
  public offerLpTemplateId: number;
  public couponGroupUuid?: string;
  public couponGroupName?: string;
  public languageConfig: { string?: ILanguageConfig };
  public isCustomStoreList?: boolean;

  public followUs: FollowUs[] = [];

  public otc: Term;

  public wisepopsId?: number;

  constructor(obj: any = {}) {
    this.offerUUID = obj.offerUUID;
    this.merchantUUID = obj.merchantUUID;
    this.offerName = obj.offerName;
    this.action = obj.action;
    this.smsBody1 = obj.smsBody1;
    this.smsBodyRegistered = obj.smsBodyRegistered;
    this.smsBodyCoolDown = obj.smsBodyCoolDown;
    this.description = obj.description;
    this.logoOverride = obj.logoOverride;
    this.logoUrl = obj.logoUrl;
    this.offerStartDate = obj.offerStartDate
      ? LocalDate.parse(obj.offerStartDate)
      : LocalDate.now();
    this.offerEndDate = obj.offerStartDate
      ? LocalDate.parse(obj.offerEndDate)
      : LocalDate.now();
    this.firstCreated = obj.offerStartDate
      ? ConvertToInstant(obj.firstCreated)
      : Instant.now();
    this.lastUpdated = obj.offerStartDate
      ? ConvertToInstant(obj.lastUpdated)
      : Instant.now();
    this.offerStatus = true;
    this.offerStaticUrl = obj.offerStaticUrl;
    this.googleTagId = obj.googleTagId;
    this.isGoogleSignup = obj.isGoogleSignup;
    this.isFacebookSignup = obj.isFacebookSignup;
    this.offerLpTemplateId = obj.offerLpTemplateId;
    this.languageConfig = obj.languageConfig;
    this.couponGroupUuid = obj.couponGroupUuid;
    this.couponGroupName = obj.couponGroupName;

    if (obj.followUsId1 && obj.followUsUrl1) {
      this.followUs.push(new FollowUs(obj.followUsId1, obj.followUsUrl1));
    }
    if (obj.followUsId2 && obj.followUsUrl2) {
      this.followUs.push(new FollowUs(obj.followUsId2, obj.followUsUrl2));
    }

    this.otc = createTerm("otc", obj);
    this.wisepopsId = obj.wisepopsId;
    this.isCustomStoreList = obj.isCustomStoreList;
  }

  public toJSON(): object {
    const { otc, ...rest } = this;
    const jsonObject = {
      ...rest,
      offerStartDate: this.offerStartDate
        ? this.offerStartDate.format(DateSubmissionFormat)
        : undefined,
      offerEndDate: this.offerEndDate
        ? this.offerEndDate.format(DateSubmissionFormat)
        : undefined,
      ...termToJson("otc", otc),
    };

    if (this.languageConfig) {
      let languageID = 1;
      [...Object.entries(this.languageConfig)].forEach(
        ([languageCode, languageConfig]) => {
          jsonObject[`languageCode${languageID}`] = languageCode;
          jsonObject[`smsBody2Lang${languageID}`] = languageConfig!.smsBody;
          jsonObject[`webBodyLang${languageID}`] = languageConfig!.webBody;
          jsonObject[`lpHeader1Lang${languageID}`] = languageConfig!.lpHeader1;
          jsonObject[`lpHeader2Lang${languageID}`] = languageConfig!.lpHeader2;
          jsonObject[`lpHeader3Lang${languageID}`] = languageConfig!.lpHeader3;
          jsonObject[`lpHeader4Lang${languageID}`] = languageConfig!.lpHeader4;
          jsonObject[`twofaBodyLang${languageID}`] = languageConfig!.twofaBody;
          languageID++;
        },
      );
    }

    if (this.followUs.length) {
      this.followUs.forEach((followUs, index) => {
        const number = index + 1;
        jsonObject[`followUsId${number}`] = followUs.id;
        jsonObject[`followUsUrl${number}`] = followUs.url;
      });
    }

    return jsonObject;
  }
}

export interface ILanguageConfig {
  languageCode: string;
  smsBody: string;
  webBody: string;
  lpHeader1: string;
  lpHeader2: string;
  lpHeader3: string;
  lpHeader4: string;
  twofaBody: string;
}

function createTerm(termName: TermNameType, obj: any): Term {
  return new Term({
    termClass: obj[termName + "TermClass"],
    termText: obj[termName + "TermText"],
    termUrl: obj[termName + "TermUrl"],
  });
}

function termToJson(termName: TermNameType, term: Term): object {
  const termJson: object = {};
  Object.entries(term.toJSON()).forEach(([key, value]) => {
    termJson[termName + key.charAt(0).toUpperCase() + key.slice(1)] = value;
  });
  return termJson;
}

export class FollowUs {
  constructor(
    public id: number,
    public url: string,
  ) {}
}
